import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";

interface ScoreFactor {
  key: string;
  label: string;
  weight: number;
  isComplete: boolean;
  fixPath: string;
}

interface CompletionScoreResponse {
  score: number;
  factors: ScoreFactor[];
  completedCount: number;
  totalCount: number;
  nextSteps: ScoreFactor[];
}

/* Factor defaults available for future use when API provides factor keys without metadata:
   channels_configured(15), pipeline_stages(15), automation_active(15), page_published(10),
   team_invited(10), integration_connected(10), brand_configured(10), form_created(5),
   custom_domain(5), analytics_tracking(5) */

function scoreColor(score: number): string {
  if (score > 70) return "text-emerald-500";
  if (score >= 40) return "text-[var(--color-warning)]";
  return "text-[var(--color-destructive)]";
}

function ringGradient(score: number): string {
  if (score > 70) return "#10b981";
  if (score >= 40) return "#f59e0b";
  return "#ef4444";
}

function CircularProgress({ score }: { score: number }) {
  const color = ringGradient(score);
  const clamped = Math.max(0, Math.min(100, score));

  return (
    <div
      className="relative mx-auto h-40 w-40 rounded-full"
      style={{
        background: `conic-gradient(${color} ${clamped * 3.6}deg, #e5e7eb ${clamped * 3.6}deg)`,
      }}
      role="img"
      aria-label={`Account completion score: ${score}%`}
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="absolute inset-3 flex items-center justify-center rounded-full bg-background dark:bg-zinc-900">
        <span className={`text-4xl font-bold ${scoreColor(score)}`}>{score}</span>
        <span className="sr-only">{score}% complete</span>
      </div>
    </div>
  );
}

function FactorRow({ factor }: { factor: ScoreFactor }) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 px-4 py-3 dark:border-zinc-700">
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={factor.isComplete}
          readOnly
          className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
          aria-label={factor.label}
        />
        <span className={factor.isComplete ? "text-zinc-500 line-through" : "text-zinc-900 dark:text-zinc-100"}>
          {factor.label}
        </span>
        <span className="text-xs text-zinc-400">({factor.weight}pts)</span>
      </div>
      {!factor.isComplete && (
        <a
          href={factor.fixPath}
          aria-label={`Fix ${factor.label}`}
          className="shrink-0 rounded-md bg-[var(--color-primary)]/5 px-3 py-1 text-sm font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 dark:bg-[var(--color-primary)]/30 dark:text-[var(--color-primary)]/70 dark:hover:bg-[var(--color-primary)]/50"
        >
          Fix
        </a>
      )}
    </li>
  );
}

export default function CompletionScore() {
  const [data, setData] = useState<CompletionScoreResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchScore = useCallback(async () => {
    try {
      setError(null);
      const res = await apiFetch<{ data: CompletionScoreResponse }>("/crm/onboarding/completion-score");
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load completion score");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScore();
  }, [fetchScore]);

  if (isLoading) {
    return (
      <div
        className="flex h-64 items-center justify-center"
        aria-busy="true"
        aria-live="polite"
      >
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-200 border-t-blue-600"
          aria-hidden="true"
        />
        <span className="sr-only">Loading account completion score...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-6 text-center dark:border-[var(--color-destructive)] dark:bg-[var(--color-destructive)]/20"
      >
        <p className="text-[var(--color-destructive)] dark:text-[var(--color-destructive)]/80">{error}</p>
        <button
          onClick={fetchScore}
          className="mt-3 rounded-md bg-[var(--color-destructive)]/10 px-4 py-2 text-sm font-medium text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/20 dark:bg-[var(--color-destructive)]/40 dark:text-[var(--color-destructive)]/70"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const completedCount = data.completedCount ?? data.factors.filter((f) => f.isComplete).length;
  const totalCount = data.totalCount ?? data.factors.length;
  const nextSteps = data.nextSteps ?? data.factors.filter((factor) => !factor.isComplete).slice(0, 3);

  return (
    <div className="rounded-xl border border-zinc-200 bg-background p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
      <h2 className="mb-1 text-lg font-semibold text-zinc-900 dark:text-zinc-100">Account Completion</h2>
      <p className="mb-6 text-sm text-zinc-500">
        {completedCount} of {totalCount} steps complete
      </p>

      <CircularProgress score={data.score} />

      <p className="mt-4 text-center text-sm text-zinc-500">
        {data.score > 70
          ? "Great progress! You're almost there."
          : data.score >= 40
            ? "Good start. Complete a few more steps to unlock the full experience."
            : "Let's get your account set up. Complete the steps below."}
      </p>

      {nextSteps.length > 0 && (
        <div className="mt-6 rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-4">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Next steps</h3>
          <ol className="mt-2 space-y-1 text-sm text-zinc-600 dark:text-zinc-300">
            {nextSteps.map((step) => (
              <li key={step.key}>{step.label}</li>
            ))}
          </ol>
        </div>
      )}

      <ul className="mt-6 space-y-2">
        {[...data.factors]
          .sort((a, b) => Number(a.isComplete) - Number(b.isComplete))
          .map((factor) => (
            <FactorRow key={factor.key} factor={factor} />
          ))}
      </ul>
    </div>
  );
}
